import {
    AutoAwesome as AutoAwesomeIcon,
    CalendarToday as CalendarIcon,
    CreditCard as CreditCardIcon,
    ExpandMore as ExpandMoreIcon,
    LightbulbOutlined as LightbulbOutlinedIcon,
    MonetizationOn as MonetizationOnIcon,
    Payment as PaymentIcon,
    Restore as RestoreIcon,
    Star as StarIcon,
    SwapHoriz as SwapHorizIcon,
    Timer as TimerIcon,
    Toll as TollIcon,
    TrendingUp as TrendingUpIcon,
    Block as BlockIcon,
} from "@mui/icons-material";
import Logger from "../../services/Logger";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    Paper,
    Typography
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { trackEvent } from "../../services/GoogleAnalytics";
import AuthService from "../../services/AuthService";
import HomeCreditService from "../../services/HomeCreditService";
import UserService from "../../services/UserService";
import UtilService from "../../services/UtilService";
import { resolveTotalPuzzleCoins } from "../../services/PuzzleCoinsUtil";
import SubscriptionService from "../../services/SubscriptionService";
import { accountHasNoAdsPurchase, noAdsCheckoutPlan, NO_ADS_PRICE_LABEL, NO_ADS_PRODUCT_KEY } from "./noAdsOffer";
import CustomSnackbar from "../components/CustomSnackbar";
import StripeClimateBadgeIframe from "../components/StripeClimateBadgeIframe";
import SubscriptionDialog from "../subscription/SubscriptionDialog";
import {
    SUBSCRIPTION_PLANS,
    DEFAULT_SELECTED_PLAN,
    PremiumPlanFeaturesSection,
    PlanPickerFootnotes,
    getSubscriptionPlanLabel,
    getSubscriptionPlanPickerEntries,
    PlanCardOfferExtras,
} from "../subscription/PanelPlan";
import CancelSubDialog from "./CancelSubDialog";
import DataDeletionRequestDialog from "./DataDeletionRequestDialog";
import ProfileInfo from "./settings/ProfileInfo";
import ChangeSubscriptionDialog from "./stripe/ChangeSubscriptionDialog";
import UserProfileService from "./UserProfileService";
import { usePagesTranslation } from "../locales";
import { SURFACE } from "../../dark-theme/palette";
import { noticeSurfaceSx } from "../../dark-theme/surfaces";

/** When true, shows the Data Deletion Request accordion and dialog on `/profile`. */
const SHOW_DATA_DELETION_REQUEST = true;

/**
 * UserProfileMain Component
 * 
 * Displays user's profile information including:
 * - Basic information (email, avatar, name, payPlan)
 * - Statistics (puzzles completed, current streak, best time)
 * - Current subscription (status and actions)
 */
const UserProfileMain = () => {
    const { t } = usePagesTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        trackEvent('entered', { name: 'UserProfileMain' });
    }, []);

    // Back-forward cache (e.g. return from Stripe checkout) restores React state; clear stuck loading flags
    useEffect(() => {
        const onPageShow = (event) => {
            if (event.persisted) {
                setUpgrading(false);
                setOpeningPortal(false);
                setReactivating(false);
            }
        };
        window.addEventListener('pageshow', onPageShow);
        return () => window.removeEventListener('pageshow', onPageShow);
    }, []);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const [cancelling, setCancelling] = useState(false);
    const [logoutCountdown, setLogoutCountdown] = useState(null);
    const [shouldAutoLogout, setShouldAutoLogout] = useState(false);
    const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
    const [changeSubscriptionDialogOpen, setChangeSubscriptionDialogOpen] = useState(false);
    const [paymentInfo, setPaymentInfo] = useState(null);
    const [loadingPaymentInfo, setLoadingPaymentInfo] = useState(false);
    const [openingPortal, setOpeningPortal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(DEFAULT_SELECTED_PLAN);
    const [upgrading, setUpgrading] = useState(false);
    const [reactivating, setReactivating] = useState(false);
    const [dataDeletionDialogOpen, setDataDeletionDialogOpen] = useState(false);
    const [dataDeletionSubmitting, setDataDeletionSubmitting] = useState(false);
    const [slidingPlayCredits, setSlidingPlayCredits] = useState(null);
    const [walletCreditsLoading, setWalletCreditsLoading] = useState(false);
    const [noAdsBusy, setNoAdsBusy] = useState(false);

    const profilePlans = useMemo(() => {
        const noAds = noAdsCheckoutPlan();
        return {
            ...SUBSCRIPTION_PLANS,
            [NO_ADS_PRODUCT_KEY]: {
                ...noAds,
                period: "one-time",
            },
        };
    }, []);
    const profilePlanEntries = useMemo(() => {
        const entries = getSubscriptionPlanPickerEntries({ hideMultiMonthPlans: true });
        const noAdsPlan = profilePlans[NO_ADS_PRODUCT_KEY];
        if (!noAdsPlan || entries.some(([key]) => key === NO_ADS_PRODUCT_KEY)) {
            return entries;
        }
        return [...entries, [NO_ADS_PRODUCT_KEY, noAdsPlan]];
    }, [profilePlans]);
    const profilePlanPickerKeys = useMemo(
        () => profilePlanEntries.map(([key]) => key),
        [profilePlanEntries],
    );

    const selectedPlanData = selectedPlan != null ? profilePlans[selectedPlan] : null;
    const noAdsAlreadyOwned = accountHasNoAdsPurchase(user?.cloudUser) || UtilService.isStandardPayPlanForUser(user?.cloudUser);
    const canCheckout = Boolean(selectedPlanData?.productKey && selectedPlanData?.priceKey)
        && !(selectedPlan === NO_ADS_PRODUCT_KEY && noAdsAlreadyOwned);

    useEffect(() => {
        if (!profilePlanPickerKeys.includes(selectedPlan)) {
            setSelectedPlan(DEFAULT_SELECTED_PLAN);
        }
    }, [profilePlanPickerKeys, selectedPlan]);

    const handleBuyNoAds = async () => {
        setNoAdsBusy(true);
        try {
            const result = await SubscriptionService.handleSubscribe("no_ads", {
                no_ads: noAdsCheckoutPlan(),
            });
            if (result.requiresLogin) {
                setSnackbarMessage(t("profile.userProfile.snackbar-login-required", "Please sign in to continue."));
                setSnackbarSeverity("info");
                setSnackbarOpen(true);
                window.location.href = "/login";
                return;
            }
            if (!result.success) {
                setSnackbarMessage(result.error || t("profile.userProfile.snackbar-upgrade-error", "Could not start checkout."));
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
            }
        } catch (error) {
            Logger.error("Error starting No Ads checkout:", error);
            setSnackbarMessage(error.message || t("profile.userProfile.snackbar-upgrade-error", "Could not start checkout."));
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        } finally {
            setNoAdsBusy(false);
        }
    };

    const loadUserData = useCallback(async () => {
        try {
            setLoading(true);
            const userInfo = await UserService.getUserInfo();
            setUser(userInfo);
        } catch (error) {
            Logger.error('Error loading user data:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const sessionId = params.get("session_id");
        if (!sessionId || sessionId.includes("{")) return undefined;
        let cancel = false;
        SubscriptionService.confirmCheckoutSession(sessionId)
            .then(() => loadUserData())
            .catch((error) => {
                Logger.error("Error confirming No Ads checkout:", error);
            })
            .finally(() => {
                if (cancel) return;
                params.delete("session_id");
                const search = params.toString();
                navigate(
                    { pathname: location.pathname, search: search ? `?${search}` : "" },
                    { replace: true },
                );
            });
        return () => {
            cancel = true;
        };
    }, [location.pathname, location.search, navigate, loadUserData]);

    const handleAutoLogout = useCallback(async () => {
        try {
            await AuthService.logout();
            navigate('/');
        } catch (error) {
            Logger.error('Error during auto-logout:', error);
            // Still navigate even if logout fails
            navigate('/');
        }
    }, [navigate]);

    // Check if subscription is canceled or expired
    const isSubscriptionInactive = useCallback((status) => {
        const upperStatus = status?.toUpperCase();
        return upperStatus === 'CANCELLED' || upperStatus === 'CANCELED' || upperStatus === 'EXPIRED';
    }, []);

    useEffect(() => {
        loadUserData();
    }, [loadUserData]);

    // Load payment info when currSub exists
    useEffect(() => {
        const loadPaymentInfo = async () => {
            const currSub = user?.cloudUser?.currSub;
            if (currSub && currSub.id) {
                try {
                    setLoadingPaymentInfo(true);
                    const info = await UserProfileService.getPaymentInfo();
                    setPaymentInfo(info);
                } catch (error) {
                    Logger.error('Error loading payment info:', error);
                    // Don't show error to user if payment info fails - it's optional
                } finally {
                    setLoadingPaymentInfo(false);
                }
            } else {
                setPaymentInfo(null);
            }
        };

        if (user) {
            loadPaymentInfo();
        }
    }, [user]);

    useEffect(() => {
        let cancelled = false;
        async function loadWalletCredits() {
            if (!user?.cloudUser?.id) {
                setSlidingPlayCredits(null);
                return;
            }
            setWalletCreditsLoading(true);
            try {
                const play = await HomeCreditService.fetchBalance(HomeCreditService.WEB_APP_CODE).catch(() => null);
                if (cancelled) return;
                setSlidingPlayCredits(play?.creditBalance ?? 0);
            } catch (error) {
                if (!cancelled) {
                    Logger.warn("UserProfileMain load wallet credits:", error);
                    setSlidingPlayCredits(0);
                }
            } finally {
                if (!cancelled) setWalletCreditsLoading(false);
            }
        }
        loadWalletCredits();
        return () => {
            cancelled = true;
        };
    }, [user?.cloudUser?.id]);

    // Auto-logout when subscription is canceled (only if cancellation was just performed)
    useEffect(() => {
        if (!shouldAutoLogout) {
            setLogoutCountdown(null);
            return;
        }

        const currSub = user?.cloudUser?.currSub || {};
        const isCanceled = isSubscriptionInactive(currSub.status) && (currSub.status?.toUpperCase() === 'CANCELLED' || currSub.status?.toUpperCase() === 'CANCELED');

        if (isCanceled && currSub.canceledAt) {
            // Start countdown
            setLogoutCountdown(10);

            const countdownInterval = setInterval(() => {
                setLogoutCountdown((prev) => {
                    if (prev === null || prev <= 1) {
                        clearInterval(countdownInterval);
                        handleAutoLogout();
                        return null;
                    }
                    return prev - 1;
                });
            }, 1000);

            return () => {
                clearInterval(countdownInterval);
            };
        } else {
            setLogoutCountdown(null);
            setShouldAutoLogout(false);
        }
    }, [user, handleAutoLogout, isSubscriptionInactive, shouldAutoLogout]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    // Extract user data from the response structure
    const cloudUser = user?.cloudUser || {};
    const stats = cloudUser.stats || {};
    const currSub = cloudUser.currSub || {};

    const assistantCreditsTotal = cloudUser.assistantCreditsTotal;

    const puzzleCoinsTotal = Math.max(0, resolveTotalPuzzleCoins(cloudUser));

    const formatAssistantCredits = (value) => {
        if (value === null || value === undefined || Number.isNaN(Number(value))) {
            return "—";
        }
        return Number(value).toLocaleString();
    };

    const formatWalletAmount = (value) => {
        if (walletCreditsLoading && (value === null || value === undefined)) {
            return "…";
        }
        if (value === null || value === undefined || Number.isNaN(Number(value))) {
            return "—";
        }
        return Number(value).toLocaleString();
    };

    // Format subscription dates (handles both timestamp numbers and date strings)
    const formatDate = (dateValue) => {
        if (!dateValue) return 'N/A';
        try {
            // Handle timestamp numbers (milliseconds)
            const date = typeof dateValue === 'number'
                ? new Date(dateValue)
                : new Date(dateValue);

            if (isNaN(date.getTime())) {
                return 'N/A';
            }

            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            return 'N/A';
        }
    };

    // Format best time
    const formatBestTime = (bestTime) => {
        if (!bestTime) return 'N/A';
        // If bestTime is in seconds, convert to MM:SS format
        if (typeof bestTime === 'number') {
            const minutes = Math.floor(bestTime / 60);
            const seconds = bestTime % 60;
            return `${minutes}:${seconds.toString().padStart(2, '0')}`;
        }
        return bestTime;
    };

    // Get subscription status color
    const getSubscriptionStatusColor = (status) => {
        switch (status?.toUpperCase()) {
            case 'ACTIVE':
                return 'success';
            case 'CANCELLED':
            case 'CANCELED':
                return 'error';
            case 'EXPIRED':
                return 'warning';
            default:
                return 'default';
        }
    };




    const handleCancelSubscription = async () => {
        try {
            setCancelling(true);
            await UserProfileService.cancelSubscription();

            setSnackbarMessage(t("profile.userProfile.snackbar-cancel-success"));
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
            setCancelDialogOpen(false);
            // Set flag to trigger auto-logout after reload
            setShouldAutoLogout(true);
            // Reload user data to reflect the cancellation
            await loadUserData();
        } catch (error) {
            Logger.error('Error cancelling subscription:', error);
            setSnackbarMessage(error.message || t("profile.userProfile.snackbar-cancel-error"));
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        } finally {
            setCancelling(false);
        }
    };

    const handleReactivateSubscription = async () => {
        try {
            setReactivating(true);
            await UserProfileService.reactivateSubscription();

            setSnackbarMessage(t("profile.userProfile.snackbar-reactivate-success"));
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
            // Reload user data to reflect the reactivation
            await loadUserData();
        } catch (error) {
            Logger.error('Error reactivating subscription:', error);
            setSnackbarMessage(error.message || t("profile.userProfile.snackbar-reactivate-error"));
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        } finally {
            setReactivating(false);
        }
    };

    const handleOpenCancelDialog = () => {
        setCancelDialogOpen(true);
    };

    const handleCloseCancelDialog = () => {
        setCancelDialogOpen(false);
    };

    const handleOpenSubscriptionDialog = () => {
        setSubscriptionModalOpen(true);
    };

    const handleUpgradeNow = async () => {
        if (!canCheckout || selectedPlan == null) {
            return;
        }
        setUpgrading(true);
        try {
            const result = await SubscriptionService.handleSubscribe(selectedPlan, profilePlans);

            if (result.requiresLogin) {
                setSnackbarMessage(t("profile.userProfile.snackbar-login-required"));
                setSnackbarSeverity("info");
                setSnackbarOpen(true);
                window.location.href = '/login';
                return;
            }

            if (!result.success) {
                setSnackbarMessage(result.error || t("profile.userProfile.snackbar-upgrade-error"));
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
                return;
            }

            // Success: SubscriptionService sets window.location to Stripe; still clear flag so
            // bfcache / back navigation does not leave the button stuck on "Processing..."
        } catch (error) {
            Logger.error('Error upgrading subscription:', error);
            setSnackbarMessage(error.message || t("profile.userProfile.snackbar-upgrade-error"));
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        } finally {
            setUpgrading(false);
        }
    };

    const handleCloseSubscriptionDialog = () => {
        setSubscriptionModalOpen(false);
    };

    const handleSubscriptionSuccess = async (plan) => {
        Logger.log('Subscription successful:', plan);
        setSubscriptionModalOpen(false);
        // Reload user data to reflect the new subscription
        await loadUserData();
        setSnackbarMessage(t("profile.userProfile.snackbar-upgrade-success"));
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
    };

    const handleOpenPaymentPortal = async () => {
        try {
            setOpeningPortal(true);
            const response = await UserProfileService.createPortalSession();
            if (response && response.portalUrl) {
                window.location.href = response.portalUrl;
            } else {
                throw new Error('Portal URL not received');
            }
        } catch (error) {
            Logger.error('Error opening payment portal:', error);
            setSnackbarMessage(error.message || t("profile.userProfile.snackbar-portal-error"));
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        } finally {
            setOpeningPortal(false);
        }
    };

    const handleOpenChangeSubscriptionDialog = () => {
        setChangeSubscriptionDialogOpen(true);
    };

    const handleCloseChangeSubscriptionDialog = () => {
        setChangeSubscriptionDialogOpen(false);
    };

    const handleOpenDataDeletionDialog = () => setDataDeletionDialogOpen(true);
    const handleCloseDataDeletionDialog = () => setDataDeletionDialogOpen(false);
    const handleDataDeletionRequest = async () => {
        setDataDeletionSubmitting(true);
        try {
            const result = await UserProfileService.requestDataDeletion();
            return result;
        } finally {
            setDataDeletionSubmitting(false);
        }
    };

    const handleChangeSubscriptionSuccess = async () => {
        setChangeSubscriptionDialogOpen(false);
        // Reload user data to reflect the subscription change
        await loadUserData();
        setSnackbarMessage(t("profile.userProfile.snackbar-change-success"));
        setSnackbarSeverity("info");
        setSnackbarOpen(true);
    };

    // Format payment method display
    const formatPaymentMethod = (paymentMethod) => {
        if (!paymentMethod) return 'N/A';

        const card = paymentMethod.card;
        if (card) {
            const brand = card.brand || '';
            const last4 = card.last4 || '';
            const expMonth = card.exp_month || '';
            const expYear = card.exp_year || '';

            return `${brand.charAt(0).toUpperCase() + brand.slice(1)} •••• ${last4} (${expMonth}/${expYear})`;
        }

        return paymentMethod.type || 'N/A';
    };

    const statsData = [
        {
            labelKey: "profile.userProfile.puzzles-completed",
            value: stats.puzzlesCompleted || 0,
            color: "success",
            Icon: TrendingUpIcon
        },
        {
            labelKey: "profile.userProfile.current-streak",
            value: stats.currentStreak ? `${stats.currentStreak} ${t("profile.userProfile.days")}` : `0 ${t("profile.userProfile.days")}`,
            color: "warning",
            Icon: CalendarIcon
        },
        {
            labelKey: "profile.userProfile.best-time",
            value: formatBestTime(stats.bestTime),
            color: "info",
            Icon: TimerIcon
        }
    ];

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 600 }}>
                {t("profile.userProfile.title")}
            </Typography>

            <Grid container spacing={3}>
                {/* Profile Information Section */}
                <ProfileInfo />

                {/* Stats Section */}
                <Grid size={{ xs: 12 }}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            {t("profile.userProfile.your-stats")}
                        </Typography>
                        <Grid container spacing={2}>
                            {statsData.map((stat, index) => {
                                const IconComponent = stat.Icon;
                                return (
                                    <Grid size={{ xs: 12, sm: 4 }} key={index}>
                                        <Box sx={{ textAlign: 'center', p: 2 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                                                <IconComponent sx={{ color: `${stat.color}.main`, fontSize: 32 }} />
                                            </Box>
                                            <Typography variant="h4" color={`${stat.color}.main`} gutterBottom>
                                                {stat.value}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {t(stat.labelKey)}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Paper>
                </Grid>

                {/* Current Subscription + AI Assistant credits (two columns on md+) */}
                <Grid size={{ xs: 12 }}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            {t("profile.userProfile.current-subscription")}
                        </Typography>
                        <Box
                            sx={{
                                mb: 2,
                                p: 2,
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: "divider",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 2,
                                flexWrap: "wrap",
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, minWidth: 0 }}>
                                <BlockIcon sx={{ color: "#3BA666", mt: 0.25 }} />
                                <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                        {t("profile.userProfile.no-ads-title", "No Ads")}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {accountHasNoAdsPurchase(cloudUser) || UtilService.isStandardPayPlanForUser(cloudUser)
                                            ? t("profile.userProfile.no-ads-owned", "Advertisements are turned off for this account.")
                                            : t(
                                                "profile.userProfile.no-ads-body",
                                                "One-time purchase. Turns off banners and other advertisements.",
                                            )}
                                    </Typography>
                                </Box>
                            </Box>
                            {accountHasNoAdsPurchase(cloudUser) || UtilService.isStandardPayPlanForUser(cloudUser) ? (
                                <Chip
                                    label={t("profile.userProfile.no-ads-on", "Ads off")}
                                    color="success"
                                    size="small"
                                />
                            ) : (
                                <Button
                                    variant="contained"
                                    onClick={handleBuyNoAds}
                                    disabled={noAdsBusy}
                                    sx={{
                                        backgroundColor: "#3BA666",
                                        "&:hover": { backgroundColor: "#2e7d32" },
                                        minHeight: 44,
                                    }}
                                >
                                    {noAdsBusy
                                        ? t("profile.userProfile.processing", "Processing...")
                                        : t("profile.userProfile.no-ads-buy", {
                                            defaultValue: "Buy {{price}}",
                                            price: NO_ADS_PRICE_LABEL,
                                        })}
                                </Button>
                            )}
                        </Box>
                        <Grid container spacing={3} alignItems="flex-start">
                            <Grid size={{ xs: 12, md: 6 }}>
                                {(cloudUser.userRole === 'CREATOR' || cloudUser.userRole === 'ADMIN') && (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {t("profile.userProfile.role-no-sub", { role: cloudUser.userRole })}
                                        </Typography>
                                    </Box>
                                )}
                                {currSub.id ? (
                                    <>
                                        <Grid container spacing={3}>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                                        {t("profile.userProfile.status")}
                                                    </Typography>
                                                    <Chip
                                                        label={currSub.status || 'N/A'}
                                                        color={getSubscriptionStatusColor(currSub.status)}
                                                        size="small"
                                                    />
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                                        {t("profile.userProfile.start-date")}
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {currSub.startAt ? formatDate(currSub.startAt) : 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                                        {t("profile.userProfile.end-date")}
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {currSub.endAt ? formatDate(currSub.endAt) : 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                                        {t("profile.userProfile.canceled-date")}
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {currSub.canceledAt ? formatDate(currSub.canceledAt) : 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            {currSub.lastPaymentAmount && (
                                                <Grid size={{ xs: 12, sm: 6 }}>
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                                            {t("profile.userProfile.last-payment")}
                                                        </Typography>
                                                        <Typography variant="body1">
                                                            {`${currSub.lastPaymentAmount} ${currSub.lastPaymentCurrency || ''}`}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            )}
                                        </Grid>
                                {isSubscriptionInactive(currSub.status) && (currSub.status?.toUpperCase() === 'CANCELLED' || currSub.status?.toUpperCase() === 'CANCELED') && logoutCountdown === null && (
                                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            variant="contained"
                                            color="success"
                                            onClick={handleReactivateSubscription}
                                            disabled={reactivating}
                                            startIcon={<RestoreIcon />}
                                            sx={{
                                                backgroundColor: '#3BA666',
                                                '&:hover': { backgroundColor: '#2e7d32' }
                                            }}
                                        >
                                            {reactivating ? t("profile.userProfile.reactivating") : t("profile.userProfile.reactivate")}
                                        </Button>
                                    </Box>
                                )}
                                {isSubscriptionInactive(currSub.status) && (currSub.status?.toUpperCase() === 'CANCELLED' || currSub.status?.toUpperCase() === 'CANCELED') && logoutCountdown !== null && (
                                    <Box sx={{ mt: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                                        <Typography variant="body2" color="error.dark" align="center">
                                            {t("profile.userProfile.canceled-logout", {
                                                count: logoutCountdown,
                                                unit: logoutCountdown !== 1 ? t("profile.userProfile.seconds") : t("profile.userProfile.second")
                                            })}
                                        </Typography>
                                    </Box>
                                )}
                                {isSubscriptionInactive(currSub.status) && (logoutCountdown === null || currSub.status?.toUpperCase() === 'EXPIRED') && (currSub.status?.toUpperCase() !== 'CANCELLED' && currSub.status?.toUpperCase() !== 'CANCELED') && (
                                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="secondary"
                                            onClick={handleOpenSubscriptionDialog}
                                            startIcon={<StarIcon />}
                                        >
                                            {t("profile.userProfile.upgrade-to-standard")}
                                        </Button>
                                    </Box>
                                )}
                                {!isSubscriptionInactive(currSub.status) && (
                                    <Box
                                        sx={{
                                            mt: 2,
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            flexWrap: 'wrap',
                                            gap: 2,
                                            width: '100%',
                                        }}
                                    >
                                        <StripeClimateBadgeIframe sx={{ mt: 0 }} />
                                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                            <Button
                                                variant="outlined"
                                                color="secondary"
                                                onClick={handleOpenChangeSubscriptionDialog}
                                                startIcon={<SwapHorizIcon />}
                                            >
                                                {t("profile.userProfile.change-plan")}
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                onClick={handleOpenCancelDialog}
                                                disabled={cancelling}
                                            >
                                                {t("profile.userProfile.cancel-subscription")}
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                            </>
                        ) : (
                            <>
                                {/* Free User Message */}
                                <Box sx={{ mb: 3, p: 2, borderRadius: 1, ...noticeSurfaceSx("info") }}>
                                    <Typography variant="body1" color="inherit">
                                        {t("profile.userProfile.free-user-msg")}
                                    </Typography>
                                </Box>

                                {/* Plan Selection */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="h6" gutterBottom>
                                        {t("profile.userProfile.choose-plan")}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, pt: 2, flexWrap: 'wrap' }}>
                                        {profilePlanEntries.map(([key, plan]) => {
                                            const noAdsCardOwned = key === NO_ADS_PRODUCT_KEY && noAdsAlreadyOwned;
                                            return (
                                            <Card
                                                key={key}
                                                sx={{
                                                    flex: {
                                                        xs: '1 1 100%',
                                                        sm: '1 1 calc(50% - 8px)',
                                                        md: '1 1 calc(25% - 12px)'
                                                    },
                                                    minWidth: { xs: '100%', sm: 0, md: 0 },
                                                    cursor: noAdsCardOwned ? 'default' : 'pointer',
                                                    border: selectedPlan === key ? '2px solid #3BA666' : `1px solid ${SURFACE.border}`,
                                                    position: 'relative',
                                                    overflow: 'visible',
                                                    opacity: noAdsCardOwned ? 0.85 : 1,
                                                    '&:hover': {
                                                        borderColor: noAdsCardOwned ? SURFACE.border : '#3BA666'
                                                    }
                                                }}
                                                onClick={() => {
                                                    if (!noAdsCardOwned) {
                                                        setSelectedPlan(key);
                                                    }
                                                }}
                                            >
                                                {plan.popular && (
                                                    <Chip
                                                        label={t("profile.userProfile.most-popular")}
                                                        size="small"
                                                        sx={{
                                                            position: 'absolute',
                                                            top: -12,
                                                            left: '50%',
                                                            transform: 'translateX(-50%)',
                                                            backgroundColor: '#3BA666',
                                                            color: 'white',
                                                            fontSize: '0.7rem',
                                                            whiteSpace: 'nowrap',
                                                            zIndex: 1
                                                        }}
                                                    />
                                                )}
                                                <CardContent sx={{ textAlign: 'center', pt: plan.popular ? 3 : 2 }}>
                                                    <Typography variant="h6" gutterBottom>
                                                        {key === NO_ADS_PRODUCT_KEY
                                                            ? t("profile.userProfile.no-ads-title", "No Ads")
                                                            : getSubscriptionPlanLabel(t, key)}
                                                    </Typography>
                                                    <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                                                        {plan.price}
                                                    </Typography>
                                                    {key === NO_ADS_PRODUCT_KEY ? (
                                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                            {noAdsCardOwned
                                                                ? t("profile.userProfile.no-ads-owned", "Advertisements are turned off for this account.")
                                                                : t(
                                                                    "profile.userProfile.no-ads-body",
                                                                    "One-time purchase. Turns off banners and other advertisements.",
                                                                )}
                                                        </Typography>
                                                    ) : plan.includesStandardMonthlyCredits ? (
                                                        <Box sx={{ mt: 0.25 }}>
                                                            <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>
                                                                {t("subs.plan.standard-monthly-credits-amount")}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                                                {t("subs.plan.standard-monthly-credits-reset")}
                                                            </Typography>
                                                        </Box>
                                                    ) : plan.period && plan.period !== "one-time" && plan.period !== "pack" ? (
                                                        <Typography variant="body2" color="text.secondary">
                                                            {t("profile.userProfile.per")} {t(`subs.plan.${key}.period`)}
                                                        </Typography>
                                                    ) : null}
                                                    <PlanCardOfferExtras planKey={key} plan={plan} />
                                                </CardContent>
                                            </Card>
                                            );
                                        })}
                                    </Box>
                                    <Box
                                        sx={{
                                            mt: 2,
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            alignItems: 'center',
                                            flexWrap: 'wrap',
                                            gap: 2,
                                            width: '100%',
                                        }}
                                    >
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            onClick={handleUpgradeNow}
                                            disabled={upgrading || !canCheckout}
                                            startIcon={<StarIcon />}
                                            sx={{
                                                backgroundColor: '#3BA666',
                                                '&:hover': { backgroundColor: '#2e7d32' }
                                            }}
                                        >
                                            {upgrading ? t("profile.userProfile.processing") : t("profile.userProfile.upgrade-now")}
                                        </Button>
                                    </Box>
                                    <PlanPickerFootnotes planKeys={profilePlanPickerKeys} />
                                </Box>

                                {/* Features List */}
                                <PremiumPlanFeaturesSection
                                    titleKey="profile.userProfile.premium-features"
                                    hideSlidingPuzzleQuest
                                />

                                <Box sx={{ mt: 2 }}>
                                    <StripeClimateBadgeIframe sx={{ mt: 0 }} />
                                </Box>
                            </>
                        )}
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                    <Box
                                        sx={{
                                            p: 2.5,
                                            borderRadius: 2,
                                            border: "1px solid",
                                            borderColor: "rgba(39, 97, 245, 0.3)",
                                            background: "linear-gradient(165deg, rgba(39, 97, 245, 0.1) 0%, rgba(59, 166, 102, 0.06) 100%)",
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                                            <TollIcon sx={{ color: "#2761F5", fontSize: 28 }} />
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "text.primary" }}>
                                                {t("profile.userProfile.play-credits")}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.55 }}>
                                            {t("profile.userProfile.play-credits-hint")}
                                        </Typography>
                                        <Box
                                            sx={{
                                                textAlign: "center",
                                                py: 1.5,
                                                px: 1,
                                                borderRadius: 1,
                                                bgcolor: "background.paper",
                                                border: "1px solid",
                                                borderColor: "divider",
                                            }}
                                        >
                                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontWeight: 600 }}>
                                                {t("profile.userProfile.play-credits-balance")}
                                            </Typography>
                                            <Typography variant="h5" sx={{ fontWeight: 800, color: "#2761F5", m: 0 }}>
                                                {formatWalletAmount(slidingPlayCredits)}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Box
                                        sx={{
                                            p: 2.5,
                                            borderRadius: 2,
                                            border: "1px solid",
                                            borderColor: "rgba(245, 158, 11, 0.35)",
                                            background: "linear-gradient(165deg, rgba(245, 158, 11, 0.12) 0%, rgba(59, 166, 102, 0.06) 100%)",
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                                            <MonetizationOnIcon sx={{ color: "#d97706", fontSize: 28 }} />
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "text.primary" }}>
                                                {t("profile.userProfile.ai-assistant-credits")}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.55 }}>
                                            {t("profile.userProfile.ai-assistant-credits-hint")}
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                                gap: 1.5,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    textAlign: "center",
                                                    py: 1.5,
                                                    px: 1,
                                                    borderRadius: 1,
                                                    bgcolor: "background.paper",
                                                    border: "1px solid",
                                                    borderColor: "divider",
                                                }}
                                            >
                                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, mb: 0.5 }}>
                                                    <LightbulbOutlinedIcon sx={{ fontSize: "1.05rem", color: "#d97706" }} />
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                        {t("profile.userProfile.coins-letter-hint")}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="h5" sx={{ fontWeight: 800, color: "#b45309", m: 0 }}>
                                                    {formatAssistantCredits(puzzleCoinsTotal)}
                                                </Typography>
                                            </Box>
                                            <Box
                                                sx={{
                                                    textAlign: "center",
                                                    py: 1.5,
                                                    px: 1,
                                                    borderRadius: 1,
                                                    bgcolor: "background.paper",
                                                    border: "1px solid",
                                                    borderColor: "divider",
                                                    boxShadow: "0 2px 10px rgba(59, 166, 102, 0.14)",
                                                }}
                                            >
                                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, mb: 0.5 }}>
                                                    <AutoAwesomeIcon sx={{ fontSize: "1.05rem", color: "#3BA666" }} />
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                        {t("profile.userProfile.coins-ai-assistant")}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="h5" sx={{ fontWeight: 800, color: "#2e7d32", m: 0 }}>
                                                    {formatAssistantCredits(assistantCreditsTotal)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* Payment Information Section */}
                {currSub.id && paymentInfo && (
                    <Grid size={{ xs: 12 }}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                {t("profile.userProfile.payment-information")}
                            </Typography>
                            {loadingPaymentInfo ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : (
                                <Grid container spacing={3}>
                                    {paymentInfo.billingEmail && (
                                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                            <Box>
                                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                                    {t("profile.userProfile.billing-email")}
                                                </Typography>
                                                <Typography variant="body1">
                                                    {paymentInfo.billingEmail}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    )}
                                    {paymentInfo.paymentMethods && Array.isArray(paymentInfo.paymentMethods) && paymentInfo.paymentMethods.length > 0 && (
                                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                                {t("profile.userProfile.payment-methods")}
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                                                {paymentInfo.paymentMethods.map((pm, index) => (
                                                    <Box
                                                        key={pm.id || index}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                            p: 1.5,
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                            borderRadius: 1
                                                        }}
                                                    >
                                                        <CreditCardIcon color="action" />
                                                        <Typography variant="body1">
                                                            {formatPaymentMethod(pm)}
                                                        </Typography>
                                                        {pm.card && pm.card.brand && (
                                                            <Chip
                                                                label={pm.card.brand.toUpperCase()}
                                                                size="small"
                                                                variant="outlined"
                                                                sx={{ ml: 'auto' }}
                                                            />
                                                        )}
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Grid>
                                    )}
                                    {paymentInfo.paymentMethods && Array.isArray(paymentInfo.paymentMethods) && paymentInfo.paymentMethods.length > 0 && paymentInfo.paymentMethods[0].created && (
                                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                            <Box>
                                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                                    {t("profile.userProfile.created-at")}
                                                </Typography>
                                                <Typography variant="body1">
                                                    {formatDate(paymentInfo.paymentMethods[0].created * 1000)}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    )}
                                    {(!paymentInfo.paymentMethods || !Array.isArray(paymentInfo.paymentMethods) || paymentInfo.paymentMethods.length === 0) && (
                                        <Grid size={{ xs: 12 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {t("profile.userProfile.no-payment-methods")}
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            )}
                            {paymentInfo && (
                                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={handleOpenPaymentPortal}
                                        disabled={openingPortal}
                                        startIcon={<PaymentIcon />}
                                    >
                                        {openingPortal ? t("profile.userProfile.opening") : t("profile.userProfile.payment-portal")}
                                    </Button>
                                </Box>
                            )}
                        </Paper>
                    </Grid>
                )}

                {SHOW_DATA_DELETION_REQUEST && (
                    <Grid size={{ xs: 12 }}>
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">
                                    {t("profile.dataDeletion.sectionTitle")}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    {t("profile.dataDeletion.sectionDesc")}
                                </Typography>
                                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={handleOpenDataDeletionDialog}
                                    >
                                        {t("profile.dataDeletion.requestButton")}
                                    </Button>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    </Grid>
                )}
            </Grid>

            {/* Payment History Section */}
            {/* <Box sx={{ mt: 4 }}>
                <PaymentHistory />
            </Box> */}

            {SHOW_DATA_DELETION_REQUEST && (
                <DataDeletionRequestDialog
                    open={dataDeletionDialogOpen}
                    onClose={handleCloseDataDeletionDialog}
                    onSubmit={handleDataDeletionRequest}
                    submitting={dataDeletionSubmitting}
                    hasActiveSubscription={!!(currSub.id && !isSubscriptionInactive(currSub.status))}
                />
            )}

            {/* Cancel Subscription Confirmation Dialog */}
            <CancelSubDialog
                open={cancelDialogOpen}
                onClose={handleCloseCancelDialog}
                onConfirm={handleCancelSubscription}
                cancelling={cancelling}
                endDate={currSub.endAt}
            />

            {/* Subscription Dialog */}
            <SubscriptionDialog
                open={subscriptionModalOpen}
                onClose={handleCloseSubscriptionDialog}
                onSubscribe={handleSubscriptionSuccess}
                triggerReason="subscription"
                userSubscription={UtilService.isStandardPayPlanForUser(cloudUser) ? 'premium' : 'free'}
            />

            {/* Change Subscription Dialog */}
            <ChangeSubscriptionDialog
                open={changeSubscriptionDialogOpen}
                onClose={handleCloseChangeSubscriptionDialog}
                onSuccess={handleChangeSubscriptionSuccess}
                currentSubscription={user}
            />

            {/* Snackbar for notifications */}
            <CustomSnackbar
                open={snackbarOpen}
                setOpen={setSnackbarOpen}
                message={snackbarMessage}
                severity={snackbarSeverity}
            />
        </Box>
    );
};

export default UserProfileMain;

